import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import { fragmentDisplayText, fragmentSource } from '../fragmentModel';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = { story: null, meta: null, fragments: [], loaded: false, loading: false, selected: 0, error: null, notice: '' };

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function wrap(value, maxChars) {
  const words = String(value || '').split(/\s+/);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    if (!word) return;
    if ((line + ' ' + word).trim().length > maxChars) {
      if (line) lines.push(line);
      line = word;
    } else {
      line = `${line} ${word}`.trim();
    }
  });
  if (line) lines.push(line);
  return lines;
}

async function loadStory() {
  if (state.loading) return;
  state.loading = true;
  state.error = null;
  try {
    const [result, fragments] = await Promise.all([
      useCoreSession.getState().startStory(),
      useCoreSession.getState().loadFragments().catch(() => []),
    ]);
    state.story = result.story || null;
    state.meta = result.state || null;
    state.fragments = fragments || [];
    state.loaded = true;
    state.selected = 0;
  } catch (error) {
    state.error = coreErrorMessage(error);
  } finally {
    state.loading = false;
  }
}

function drawTop(ctx, width) {
  const session = useCoreSession.getState();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // JOURNAL', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, 'ACTIVE RECORD // BRONX', 232, 5, TERMINAL.TEXT_DIM);
  text(ctx, session.status === 'online' ? 'RECORD' : 'OFFLINE', width - 64, 5, session.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawNode(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'STORY // RECORD');
  const node = state.story?.node;
  if (!node) {
    text(ctx, state.error || (state.loading ? 'SYNCING...' : 'NO RECORD.'), x + 12, y + 30, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    return;
  }
  text(ctx, `ACT ${state.meta?.act ?? state.story?.act ?? '--'} // ${String(state.meta?.status || 'active').toUpperCase()}`, x + 12, y + 28, TERMINAL.TEXT_DIM);
  text(ctx, String(node.speaker || 'NARRATOR').toUpperCase(), x + 12, y + 48, TERMINAL.GREEN);
  const maxChars = Math.max(16, Math.floor((w - 28) / 8));
  const lines = wrap(node.text, maxChars).slice(0, Math.floor((h - 82) / 16));
  lines.forEach((line, index) => text(ctx, line, x + 12, y + 70 + index * 16, TERMINAL.TEXT));
  rule(ctx, x + 12, y + h - 24, w - 24);
  const footer = state.error || state.notice || `NODE ${node.nodeId}`;
  text(ctx, footer.slice(0, maxChars), x + 12, y + h - 18, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
}

function drawFragments(ctx, x, y, w, h, twoLine = false) {
  const decryptedCount = state.fragments.filter((fragment) => fragment.visibility === 'DECRYPTED').length;
  DopeUI.Panel(ctx, x, y, w, h, `CANON // EXPEDIENTE ${decryptedCount}/${state.fragments.length || 4}`);
  const rowH = (h - 34) / Math.max(1, Math.min(4, state.fragments.length || 4));
  const maxChars = Math.max(18, Math.floor((w - 24) / 8));
  const clip = (value, limit) => String(value || '').slice(0, limit);
  state.fragments.forEach((fragment, index) => {
    const by = y + 28 + index * rowH;
    const decrypted = fragment.visibility === 'DECRYPTED';
    const title = clip(fragment.title || 'UNTITLED', maxChars - 18);
    const source = clip(fragmentSource(fragment), Math.max(8, Math.floor(maxChars / 2) - 2));
    const body = clip(fragmentDisplayText(fragment), Math.max(8, maxChars - source.length - 5));
    if (twoLine) {
      text(ctx, `${index + 1} ${fragment.visibility} // ${title}`, x + 12, by, decrypted ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
      text(ctx, `${source} // ${body}`, x + 12, by + 16, decrypted ? TERMINAL.TEXT : TERMINAL.TEXT_DIM);
    } else {
      text(ctx, clip(`${index + 1} ${fragment.visibility} // ${title} // ${body}`, maxChars), x + 12, by, decrypted ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    }
  });
  if (!state.fragments.length) text(ctx, 'NO EXPEDIENTE // ACQUIRE HRONIR', x + 12, y + 30, TERMINAL.TEXT_DIM);
}

function drawChoices(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'CHOICES');
  const choices = state.story?.choices || [];
  if (!choices.length) {
    text(ctx, 'NO CHOICES LEFT.', x + 12, y + 30, TERMINAL.TEXT_DIM);
    text(ctx, 'THE RECORD IS CLOSED.', x + 12, y + 48, TERMINAL.GREEN);
    return;
  }
  const maxChars = Math.max(12, Math.floor((w - 32) / 8));
  const choiceH = 44;
  choices.forEach((choice, index) => {
    const rowY = y + 30 + index * (choiceH + 2);
    const selected = index === state.selected;
    const available = choice.available !== false;
    if (selected) {
      ctx.fillStyle = available ? TERMINAL.GREEN_DARK : TERMINAL.BLACK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, choiceH);
    }
    const lines = wrap(choice.label, maxChars).slice(0, 2);
    lines.forEach((line, lineIndex) => {
      const color = !available
        ? TERMINAL.TEXT_DIM
        : selected
          ? TERMINAL.TEXT_BRIGHT
          : TERMINAL.TEXT;
      text(ctx, `${lineIndex === 0 ? (selected ? '>' : ' ') : ' '} ${line}`, x + 12, rowY + lineIndex * 15, color);
    });
    if (available) {
      interactiveRegistry.register({
        id: `journal-choice-${index}`,
        x: x + 6,
        y: rowY - 4,
        w: w - 12,
        h: choiceH,
        onClick: () => { state.selected = index; state.notice = ''; },
      });
    }
  });

  const canChoose = !state.loading
    && choices.length > 0
    && choices[state.selected]?.available !== false;
  const actionH = w > 300 ? 44 : 24;
  const actionY = y + h - actionH - 12;
  DopeUI.Button(ctx, x + 12, actionY, w - 24, actionH, state.loading ? 'SENDING' : 'CHOOSE', canChoose ? 'hover' : 'disabled');
  if (canChoose) {
    interactiveRegistry.register({
      id: 'journal-choose',
      x: x + 12,
      y: actionY,
      w: w - 24,
      h: actionH,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        try {
          const result = await useCoreSession.getState().chooseStory(state.selected);
          state.story = result.story || state.story;
          state.meta = result.state || state.meta;
          state.fragments = await useCoreSession.getState().loadFragments().catch(() => state.fragments);
          state.notice = result.pendingEffects?.length
            ? `${result.pendingEffects.length} EFFECT(S) PENDING`
            : 'RECORD ADVANCED';
          state.selected = 0;
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
}

registerView('journal', function renderJournal(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  if (!state.loaded && !state.loading && useCoreSession.getState().status === 'online') loadStory();

  drawTop(ctx, safe.width);
  const mobile = safe.width < 500;
  if (mobile) {
    drawNode(ctx, 8, 32, safe.width - 16, 150);
    drawFragments(ctx, 8, 186, safe.width - 16, 180, true);
    drawChoices(ctx, 8, 370, safe.width - 16, 160);
  } else {
    drawNode(ctx, 8, 32, 376, 150);
    drawFragments(ctx, 8, 186, 376, 132);
    drawChoices(ctx, 400, 32, 232, 288);
  }

  const footerY = mobile ? 536 : 328;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER SELECT   ESC DESKTOP', 8, mobile ? footerY + 8 : 360, TERMINAL.TEXT_DIM);
  const backX = mobile ? 8 : safe.width - 200;
  const backY = mobile ? 588 : 336;
  const backW = mobile ? safe.width - 16 : 192;
  const backH = mobile ? 44 : 24;
  DopeUI.Button(ctx, backX, backY, backW, backH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'journal-back', x: backX, y: backY, w: backW, h: backH,
    onClick: () => navigateTo('main_menu'),
  });
  if (!mobile) text(ctx, 'JOURNAL // THE RECORD REMEMBERS.', 8, 384, TERMINAL.GREEN);
});
